import { useEffect, useState } from "react";
import axios from "axios";
import {
  API, authHeaders, fmt, StatusBadge,
  TH, TD, AdminCard, PageHeading,
  Modal, ModalHeader, inputStyle,
} from "./AdminUtils";

const ORDER_STATUSES = ["PENDING", "CONFIRMED", "SHIPPED", "DELIVERED", "CANCELLED"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // order detail panel
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterPayment, setFilterPayment] = useState("ALL");
  const [actionLoading, setActionLoading] = useState("");
  const [refundMode, setRefundMode] = useState(null); // null | "full" | "partial"
  const [partialAmt, setPartialAmt] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [userSearchResult, setUserSearchResult] = useState(null);
  const [newStatus, setNewStatus] = useState("PENDING");

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/orders`, { headers: authHeaders() })
      .then((r) => {
        const sorted = [...(r.data.content || [])].sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
        setOrders(sorted);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // When selected changes, update the newStatus default
  useEffect(() => {
    if (selected) setNewStatus(selected.status || "PENDING");
  }, [selected]);

  const act = async (label, fn) => {
    setActionLoading(label);
    try {
      await fn();
      load();
      // re-fetch the selected order to update its details
      if (selected) {
        const res = await axios.get(`${API}/admin/orders/${selected.id}`, { headers: authHeaders() });
        setSelected(res.data);
      }
    } catch (e) {
      alert(e.response?.data?.message || e.response?.data || e.message || "Action failed");
    } finally {
      setActionLoading("");
    }
  };

  const searchByEmail = async () => {
    if (!userSearch.trim()) return;
    try {
      const res = await axios.get(`${API}/admin/orders/user/email/${encodeURIComponent(userSearch.trim())}`, { headers: authHeaders() });
      setUserSearchResult(res.data);
    } catch {
      setUserSearchResult([]);
    }
  };

  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    const matchSearch =
      !search ||
      String(o.id).includes(q) ||
      o.userName?.toLowerCase().includes(q) ||
      o.userEmail?.toLowerCase().includes(q) ||
      o.razorpayOrderId?.toLowerCase().includes(q);
    const matchPayment = filterPayment === "ALL" || o.paymentStatus === filterPayment;
    const matchStatus = filterStatus === "ALL" || o.status === filterStatus;
    return matchSearch && matchPayment && matchStatus;
  });

  const statBox = (label, count, color) => (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border-subtle)",
      borderRadius: "var(--radius-lg)",
      padding: "14px 20px",
      minWidth: 120,
    }}>
      <div style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>{label}</div>
      <div style={{ color, fontWeight: 700, fontSize: 22, fontFamily: "var(--font-serif)" }}>{count}</div>
    </div>
  );

  const paidCount = orders.filter(o => o.paymentStatus === "PAID").length;
  const pendingCount = orders.filter(o => o.paymentStatus !== "PAID").length;
  const refundedCount = orders.filter(o => o.status === "REFUNDED").length;

  const selectStyle = { ...inputStyle, cursor: "pointer" };

  return (
    <div>
      <PageHeading title="Orders" sub={`${orders.length} total orders`} />

      {/* Quick stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        {statBox("Total", orders.length, "var(--text-primary)")}
        {statBox("Paid", paidCount, "var(--success-text)")}
        {statBox("Pending", pendingCount, "var(--warning-text)")}
        {statBox("Refunded", refundedCount, "#c084fc")}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          placeholder="Search ID, name, email, Razorpay ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: 300 }}
        />
        <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)} style={{ ...selectStyle, width: 160 }}>
          <option value="ALL">All Payments</option>
          <option value="PAID">Paid</option>
          <option value="CREATED">Created</option>
          <option value="COD">COD</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...selectStyle, width: 160 }}>
          <option value="ALL">All Statuses</option>
          {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          <option value="REFUNDED">REFUNDED</option>
        </select>
      </div>

      {/* Search by user email */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 8, maxWidth: 460 }}>
          <input
            placeholder="Find orders by user email…"
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") searchByEmail(); }}
            style={inputStyle}
          />
          <button onClick={searchByEmail} className="tc-btn tc-btn-secondary" style={{ whiteSpace: "nowrap", padding: "9px 16px" }}>Search</button>
          {userSearchResult && (
            <button onClick={() => setUserSearchResult(null)} className="tc-btn tc-btn-ghost" style={{ whiteSpace: "nowrap" }}>Clear</button>
          )}
        </div>
        {userSearchResult !== null && (
          <div style={{ marginTop: 8, color: "var(--text-muted)", fontSize: 12 }}>
            {userSearchResult.length} order{userSearchResult.length !== 1 ? "s" : ""} for <strong style={{ color: "var(--accent)" }}>{userSearch}</strong>
          </div>
        )}
      </div>

      {/* Order detail modal */}
      {selected && (
        <Modal onClose={() => { setSelected(null); setRefundMode(null); }} width={580}>
          <ModalHeader title={`Order #${selected.id}`} onClose={() => { setSelected(null); setRefundMode(null); }} />

          {/* Info grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            {[
              { label: "Customer", value: selected.userName },
              { label: "Email", value: selected.userEmail },
              { label: "Amount", value: fmt(selected.amount) },
              { label: "Date", value: selected.orderDate ? new Date(selected.orderDate).toLocaleString("en-IN") : "—" },
              { label: "Razorpay Order ID", value: selected.razorpayOrderId },
              { label: "Address", value: selected.address },
            ].map((f) => (
              <div key={f.label}>
                <div style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{f.label}</div>
                <div style={{ color: "var(--text-primary)", fontSize: 13, wordBreak: "break-all" }}>{f.value || "—"}</div>
              </div>
            ))}
          </div>

          {/* Status badges */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <StatusBadge value={selected.paymentStatus} />
            <StatusBadge value={selected.status} />
          </div>

          {/* Items */}
          {selected.items?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>Order Items</div>
              <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
                {selected.items.map((item, i) => (
                  <div key={i} style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 16px",
                    borderBottom: i < selected.items.length - 1 ? "1px solid var(--border-subtle)" : "none",
                  }}>
                    <span style={{ color: "var(--text-primary)", fontSize: 13 }}>
                      {item.productName} <span style={{ color: "var(--text-muted)" }}>× {item.quantity}</span>
                    </span>
                    <span style={{ color: "var(--accent)", fontWeight: 600, fontSize: 13 }}>
                      {fmt(item.price * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <hr style={{ border: "none", borderTop: "1px solid var(--border-subtle)", margin: "20px 0" }} />

          {/* ── Actions ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Update status */}
            <div>
              <div style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Update Order Status</div>
              <div style={{ display: "flex", gap: 8 }}>
                <select value={newStatus} onChange={e => setNewStatus(e.target.value)} style={{ ...selectStyle, flex: 1 }}>
                  {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button
                  onClick={() => act("status", () =>
                    axios.put(`${API}/admin/orders/${selected.id}/status`, null, {
                      params: { status: newStatus },
                      headers: authHeaders(),
                    })
                  )}
                  disabled={!!actionLoading}
                  style={{
                    background: "var(--success-bg)", border: "1px solid var(--success-border)",
                    color: "var(--success-text)", padding: "9px 18px",
                    borderRadius: "var(--radius-sm)", fontSize: 12, whiteSpace: "nowrap",
                  }}
                >
                  {actionLoading === "status" ? "…" : "Set Status"}
                </button>
              </div>
            </div>

            {/* Process / Cancel */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => act("process", () =>
                  axios.post(`${API}/admin/orders/${selected.id}/process`, null, { headers: authHeaders() })
                )}
                disabled={!!actionLoading}
                style={{
                  flex: 1, background: "var(--bg-secondary)",
                  border: "1px solid var(--border-default)",
                  color: "var(--text-secondary)", padding: "9px",
                  borderRadius: "var(--radius-sm)", fontSize: 12,
                }}
              >
                {actionLoading === "process" ? "…" : "⟶ Advance Pipeline"}
              </button>
              <button
                onClick={() => act("cancel", () =>
                  axios.post(`${API}/admin/orders/${selected.id}/cancel`, null, { headers: authHeaders() })
                )}
                disabled={!!actionLoading}
                style={{
                  flex: 1, background: "var(--danger-bg)",
                  border: "1px solid var(--danger-border)",
                  color: "var(--danger-text)", padding: "9px",
                  borderRadius: "var(--radius-sm)", fontSize: 12,
                }}
              >
                {actionLoading === "cancel" ? "…" : "✕ Cancel Order"}
              </button>
            </div>

            {/* COD — Mark as Collected */}
            {selected.paymentStatus === "COD" && (
              <div style={{ border: "1px solid #3d2e00", borderRadius: "var(--radius-sm)", padding: 16, background: "#1a1200" }}>
                <div style={{ color: "#f59e0b", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
                  Cash on Delivery
                </div>
                <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
                  Mark this order as collected once the delivery agent has received cash from the customer.
                </p>
                <button
                  onClick={() => act("cod-collect", () =>
                    axios.post(`${API}/admin/orders/${selected.id}/collect-cod`, null, { headers: authHeaders() })
                  )}
                  disabled={!!actionLoading}
                  style={{
                    width: "100%", background: "#3d2e00", border: "1px solid #f59e0b",
                    color: "#f59e0b", padding: "10px",
                    borderRadius: "var(--radius-sm)", fontSize: 12, fontWeight: 500,
                  }}
                >
                  {actionLoading === "cod-collect" ? "Processing…" : "✓ Mark Cash as Collected"}
                </button>
              </div>
            )}

            {/* Refund section */}
            <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)", padding: 16 }}>
              <div style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
                Razorpay Refund
              </div>
              {!refundMode ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setRefundMode("full")}
                    style={{
                      flex: 1, background: "#1a0d2e", border: "1px solid #2e1a4e",
                      color: "#c084fc", padding: "9px", borderRadius: "var(--radius-sm)", fontSize: 12,
                    }}
                  >
                    Full Refund ({fmt(selected.amount)})
                  </button>
                  <button
                    onClick={() => setRefundMode("partial")}
                    style={{
                      flex: 1, background: "var(--bg-secondary)", border: "1px solid var(--border-default)",
                      color: "var(--text-secondary)", padding: "9px", borderRadius: "var(--radius-sm)", fontSize: 12,
                    }}
                  >
                    Partial Refund
                  </button>
                </div>
              ) : refundMode === "full" ? (
                <div>
                  <p style={{ color: "var(--warning-text)", fontSize: 12, marginBottom: 12 }}>
                    ⚠ This will issue a full refund of <strong>{fmt(selected.amount)}</strong> via Razorpay. This cannot be undone.
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => act("refund", () =>
                        axios.post(`${API}/admin/orders/${selected.id}/refund`, null, { headers: authHeaders() })
                      )}
                      disabled={!!actionLoading}
                      style={{
                        flex: 1, background: "#1a0d2e", border: "1px solid #2e1a4e",
                        color: "#c084fc", padding: "9px", borderRadius: "var(--radius-sm)", fontSize: 12,
                      }}
                    >
                      {actionLoading === "refund" ? "Processing…" : "Confirm Full Refund"}
                    </button>
                    <button onClick={() => setRefundMode(null)} className="tc-btn tc-btn-secondary" style={{ padding: "9px 14px" }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  <label style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 8 }}>
                    Refund Amount (₹)
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="number"
                      placeholder={`Max: ${selected.amount}`}
                      value={partialAmt}
                      onChange={e => setPartialAmt(e.target.value)}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button
                      onClick={() => act("refundP", () =>
                        axios.post(`${API}/admin/orders/${selected.id}/refund/partial`, null, {
                          params: { amount: partialAmt },
                          headers: authHeaders(),
                        })
                      )}
                      disabled={!!actionLoading || !partialAmt}
                      style={{
                        background: "#1a0d2e", border: "1px solid #2e1a4e",
                        color: "#c084fc", padding: "9px 18px",
                        borderRadius: "var(--radius-sm)", fontSize: 12,
                      }}
                    >
                      {actionLoading === "refundP" ? "…" : "Refund"}
                    </button>
                    <button onClick={() => setRefundMode(null)} className="tc-btn tc-btn-secondary" style={{ padding: "9px 14px" }}>Cancel</button>
                  </div>
                </div>
              )}

              {selected.paymentStatus === "COD" && (
                <p style={{ color: "var(--warning-text)", fontSize: 11, marginTop: 10 }}>
                  ⚠ Razorpay refunds do not apply to COD orders.
                </p>
              )}
              {!selected.razorpayOrderId && selected.paymentStatus !== "COD" && (
                <p style={{ color: "var(--danger-text)", fontSize: 11, marginTop: 10 }}>
                  ⚠ No Razorpay payment ID on this order — refund unavailable for older orders.
                </p>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Orders table */}
      <AdminCard>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TH>ID</TH>
              <TH>Customer</TH>
              <TH>Amount</TH>
              <TH>Payment</TH>
              <TH>Status</TH>
              <TH>Date</TH>
              <TH>Actions</TH>
            </tr>
          </thead>
          <tbody>
            {(userSearchResult ?? filtered).map((o) => (
              <tr key={o.id}
                style={{ transition: "background var(--transition)", cursor: "default" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-hover)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <TD style={{ color: "var(--accent)", fontWeight: 500 }}>#{o.id}</TD>
                <TD>
                  <div style={{ color: "var(--text-primary)", fontSize: 13 }}>{o.userName || "—"}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 1 }}>{o.userEmail || ""}</div>
                </TD>
                <TD style={{ color: "var(--accent)", fontWeight: 600 }}>{fmt(o.amount)}</TD>
                <TD><StatusBadge value={o.paymentStatus} /></TD>
                <TD><StatusBadge value={o.status} /></TD>
                <TD style={{ color: "var(--text-muted)", fontSize: 12 }}>
                  {o.orderDate ? new Date(o.orderDate).toLocaleDateString("en-IN") : "—"}
                </TD>
                <TD>
                  <button
                    onClick={() => setSelected(o)}
                    style={{
                      background: "var(--bg-secondary)",
                      border: "1px solid var(--border-default)",
                      color: "var(--text-secondary)",
                      padding: "4px 14px",
                      borderRadius: "var(--radius-sm)",
                      fontSize: 11,
                      letterSpacing: "0.04em",
                    }}
                  >
                    Manage →
                  </button>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && (
          <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
            <div className="tc-spinner" />
          </div>
        )}
        {!loading && (userSearchResult ?? filtered).length === 0 && (
          <div className="tc-empty" style={{ padding: 48 }}>
            <div className="tc-empty-dot" />
            No orders found
          </div>
        )}
      </AdminCard>
    </div>
  );
}